// =====================================================================
// PLENARIUM:CENTRAL_ATAS -- Central de Atas
//  - Lista das sessoes com a situacao da ata.
//  - Enviar o audio/video, gerar a ata (como antes, agora aqui).
//  - Editar a ata direto no sistema e conversar com a IA: ela propoe a
//    alteracao, a tela mostra o que muda e voce aplica ou descarta.
//  - Transcricao na integra, SOMENTE LEITURA, com codigo de verificacao
//    (prova de que continua igual ao que saiu do audio).
//  - Versoes: cada mudanca guarda o texto anterior (conferir e restaurar).
//  - PDF e Word como antes.
// =====================================================================
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import { FileText, Download, Save, Sparkles, Send, Upload, RotateCcw, Search, X, Copy, Lock, Check, BookOpen } from 'lucide-react';
import { gerarPDFAtaIA, gerarWordAtaIA } from './Relatorios';

const ST = {
  SEM: ['Sem ata', '#64748b', '#f1f5f9'], PENDENTE: ['Na fila', '#b45309', '#fef3c7'], TRANSCREVENDO: ['Transcrevendo…', '#b45309', '#fef3c7'],
  MONTANDO: ['Redigindo a ata…', '#7c3aed', '#ede9fe'], CONCLUIDA: ['Ata pronta', '#15803d', '#dcfce7'], ERRO: ['Erro', '#b91c1c', '#fee2e2']
};
const ORIGEM = { MANUAL: 'edição manual', IA: 'ajuste pela IA', RESTAURADA: 'restauração de versão', ANTES_DE_GERAR: 'nova geração da ata' };
const dataBR = (d) => { try { const x = new Date(d); return new Date(x.getTime() + x.getTimezoneOffset() * 60000).toLocaleDateString('pt-BR'); } catch (e) { return ''; } };
const quando = (d) => { try { return new Date(d).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }); } catch (e) { return ''; } };
const erroDe = (e, p) => (e && e.response && e.response.data && e.response.data.error) || p;
const semAcento = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

// ---- o que mudou (por paragrafo; dentro do paragrafo, por palavra) ----
function diffPalavras(a, b) {
  const A = a.split(/(\s+)/), B = b.split(/(\s+)/);
  if (A.length * B.length > 250000) return null;
  const m = A.length, n = B.length; const L = Array.from({ length: m + 1 }, () => new Uint16Array(n + 1));
  for (let i = m - 1; i >= 0; i--) for (let j = n - 1; j >= 0; j--) L[i][j] = A[i] === B[j] ? L[i + 1][j + 1] + 1 : Math.max(L[i + 1][j], L[i][j + 1]);
  const out = []; let i = 0, j = 0;
  while (i < m && j < n) { if (A[i] === B[j]) { out.push(['=', A[i]]); i++; j++; } else if (L[i + 1][j] >= L[i][j + 1]) { out.push(['-', A[i]]); i++; } else { out.push(['+', B[j]]); j++; } }
  while (i < m) out.push(['-', A[i++]]); while (j < n) out.push(['+', B[j++]]);
  return out;
}
function diffAta(antes, depois) {
  const A = String(antes || '').split(/\n{2,}/), B = String(depois || '').split(/\n{2,}/);
  let i = 0; while (i < A.length && i < B.length && A[i] === B[i]) i++;
  let j = 0; while (j < A.length - i && j < B.length - i && A[A.length - 1 - j] === B[B.length - 1 - j]) j++;
  return { removidos: A.slice(i, A.length - j), adicionados: B.slice(i, B.length - j), iguaisAntes: i, iguaisDepois: j };
}
function MostrarDiff({ antes, depois }) {
  const d = useMemo(() => diffAta(antes, depois), [antes, depois]);
  if (!d.removidos.length && !d.adicionados.length) return <p style={{ color: '#64748b', fontSize: '.85rem' }}>A IA não mudou nada no texto.</p>;
  const pares = d.removidos.length === d.adicionados.length ? d.removidos.map((r, k) => diffPalavras(r, d.adicionados[k])) : null;
  return (
    <div className="pca-diff">
      {d.iguaisAntes > 0 && <div className="pca-diff-ctx">… {d.iguaisAntes} parágrafo(s) sem mudança …</div>}
      {pares && pares.every(Boolean)
        ? pares.map((p, k) => <p key={k}>{p.map(([t, s], q) => t === '=' ? <span key={q}>{s}</span> : t === '-' ? <del key={q}>{s}</del> : <ins key={q}>{s}</ins>)}</p>)
        : <>{d.removidos.map((r, k) => <p key={'r' + k}><del>{r}</del></p>)}{d.adicionados.map((r, k) => <p key={'a' + k}><ins>{r}</ins></p>)}</>}
      {d.iguaisDepois > 0 && <div className="pca-diff-ctx">… {d.iguaisDepois} parágrafo(s) sem mudança …</div>}
    </div>
  );
}

const CSS = `
.pca { display: grid; grid-template-columns: 300px 1fr; gap: 14px; align-items: start; }
@media (max-width: 980px) { .pca { grid-template-columns: 1fr; } }
.pca-lista { background: #fff; border: 1px solid var(--cinza, #e2e8f0); border-radius: 12px; padding: 10px; max-height: calc(100vh - 160px); overflow-y: auto; position: sticky; top: 10px; }
.pca-busca { position: relative; margin-bottom: 8px; } .pca-busca input { width: 100%; box-sizing: border-box; padding: 8px 10px 8px 30px; border: 1px solid #cbd5e1; border-radius: 8px; font: inherit; }
.pca-busca svg { position: absolute; left: 9px; top: 10px; color: #94a3b8; }
.pca-s { width: 100%; text-align: left; border: 1px solid transparent; background: none; border-radius: 10px; padding: 9px 10px; cursor: pointer; font: inherit; display: block; color: #0f172a; }
.pca-s:hover { background: #f8fafc; } .pca-s.on { background: #eff6ff; border-color: #bfdbfe; }
.pca-s b { display: block; font-size: .85rem; } .pca-s small { color: #64748b; font-size: .74rem; }
.pca-chip { display: inline-block; font-size: .68rem; font-weight: 800; border-radius: 999px; padding: 2px 8px; margin-left: 6px; }
.pca-card { background: #fff; border: 1px solid var(--cinza, #e2e8f0); border-radius: 12px; padding: 16px; }
.pca-topo { display: flex; justify-content: space-between; align-items: flex-start; gap: 10px; flex-wrap: wrap; margin-bottom: 12px; }
.pca-topo h2 { font-size: 1.05rem; margin: 0; } .pca-topo small { color: #64748b; }
.pca-bts { display: flex; gap: 6px; flex-wrap: wrap; }
.pca-abas { display: flex; gap: 6px; border-bottom: 1px solid #e2e8f0; margin-bottom: 12px; flex-wrap: wrap; }
.pca-aba { border: 0; background: none; padding: 9px 12px; font: inherit; font-weight: 700; color: #64748b; cursor: pointer; border-bottom: 3px solid transparent; }
.pca-aba.on { color: #1d4ed8; border-bottom-color: #1d4ed8; }
.pca-edit { display: grid; grid-template-columns: 1fr 340px; gap: 12px; }
@media (max-width: 1250px) { .pca-edit { grid-template-columns: 1fr; } }
.pca-texto { width: 100%; box-sizing: border-box; min-height: 62vh; font-family: 'Times New Roman', Times, serif; font-size: 15px; line-height: 1.7; padding: 14px; border: 1px solid #cbd5e1; border-radius: 10px; resize: vertical; }
.pca-chat { border: 1px solid #e2e8f0; border-radius: 10px; display: flex; flex-direction: column; max-height: 70vh; background: #fafaff; }
.pca-chat-cab { padding: 10px 12px; border-bottom: 1px solid #e2e8f0; font-weight: 800; display: flex; align-items: center; gap: 6px; color: #5b21b6; }
.pca-msgs { flex: 1; overflow-y: auto; padding: 10px; display: flex; flex-direction: column; gap: 8px; min-height: 120px; }
.pca-msg { border-radius: 10px; padding: 8px 10px; font-size: .84rem; max-width: 92%; white-space: pre-wrap; }
.pca-msg.u { background: #1d4ed8; color: #fff; align-self: flex-end; } .pca-msg.ia { background: #ede9fe; color: #3b0764; align-self: flex-start; }
.pca-msg small { display: block; opacity: .75; font-size: .68rem; margin-top: 3px; }
.pca-enviar { border-top: 1px solid #e2e8f0; padding: 8px; display: flex; gap: 6px; }
.pca-enviar textarea { flex: 1; resize: none; min-height: 54px; border: 1px solid #cbd5e1; border-radius: 8px; padding: 7px 9px; font: inherit; font-size: .85rem; }
.pca-prop { border: 2px solid #7c3aed; border-radius: 12px; padding: 12px; margin-bottom: 12px; background: #faf5ff; }
.pca-diff { max-height: 40vh; overflow-y: auto; background: #fff; border: 1px solid #e9d5ff; border-radius: 8px; padding: 10px; font-family: 'Times New Roman', Times, serif; font-size: 14px; line-height: 1.6; }
.pca-diff p { margin: 0 0 8px; white-space: pre-wrap; } .pca-diff del { background: #fee2e2; color: #991b1b; } .pca-diff ins { background: #dcfce7; color: #166534; text-decoration: none; }
.pca-diff-ctx { color: #94a3b8; font-size: .78rem; font-family: inherit; margin: 4px 0; }
.pca-trans { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 10px; padding: 14px; white-space: pre-wrap; font-size: .9rem; line-height: 1.7; max-height: 65vh; overflow-y: auto; color: #1e293b; user-select: text; }
.pca-selo { display: flex; gap: 8px; align-items: flex-start; border-radius: 10px; padding: 10px 12px; margin-bottom: 10px; font-size: .8rem; }
.pca-vazio { text-align: center; color: #64748b; padding: 30px 12px; }
.pca-ver { display: flex; justify-content: space-between; gap: 10px; border-bottom: 1px solid #f1f5f9; padding: 9px 0; align-items: center; flex-wrap: wrap; }
`;

export default function CentralAtas() {
  const location = useLocation();
  const navigate = useNavigate();
  const [sessoes, setSessoes] = useState(null);
  const [busca, setBusca] = useState('');
  const [sel, setSel] = useState(() => new URLSearchParams(location.search).get('sessao') || null);
  const [ataId, setAtaId] = useState(null);
  const [det, setDet] = useState(null);
  const [texto, setTexto] = useState('');
  const [aba, setAba] = useState('ata');
  const [salvando, setSalvando] = useState(false);
  const [arquivo, setArquivo] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [pedido, setPedido] = useState('');
  const [pensando, setPensando] = useState(null);
  const [proposta, setProposta] = useState(null);
  const [verVersao, setVerVersao] = useState(null);
  const msgsRef = useRef(null);

  const carregarSessoes = useCallback(async () => {
    try { const r = await axios.get('/api/ata-ia/central/sessoes'); setSessoes(Array.isArray(r.data) ? r.data : []); }
    catch (e) { setSessoes([]); toast.error(erroDe(e, 'Não foi possível carregar as sessões.')); }
  }, []);
  useEffect(() => { carregarSessoes(); }, [carregarSessoes]);

  const sessao = useMemo(() => (sessoes || []).find(s => s.id === sel) || null, [sessoes, sel]);
  const atasDaSessao = sessao ? sessao.atas || [] : [];
  const primeiraAta = atasDaSessao.length ? atasDaSessao[0].id : null;
  useEffect(() => { setAtaId(primeiraAta); setProposta(null); setVerVersao(null); }, [sel, primeiraAta]); // troca de sessao (ou chegou gravacao nova)

  const carregarAta = useCallback(async (manterTexto) => {
    if (!ataId) { setDet(null); setTexto(''); return; }
    try {
      const r = await axios.get(`/api/ata-ia/${ataId}/completo`);
      setDet(r.data);
      if (!manterTexto) setTexto((r.data.ata && r.data.ata.textoAta) || '');
    } catch (e) { toast.error(erroDe(e, 'Não foi possível abrir a ata.')); }
  }, [ataId]);
  useEffect(() => { carregarAta(false); }, [carregarAta]);

  const status = det && det.ata ? det.ata.status : null;
  const processando = ['PENDENTE', 'TRANSCREVENDO', 'MONTANDO'].includes(status);
  useEffect(() => {
    if (!processando) return undefined;
    const t = setInterval(async () => {
      try {
        const r = await axios.get(`/api/ata-ia/${ataId}`);
        if (!['PENDENTE', 'TRANSCREVENDO', 'MONTANDO'].includes(r.data.status)) { await carregarAta(false); carregarSessoes(); }
        else setDet(d => (d ? { ...d, ata: { ...d.ata, status: r.data.status } } : d));
      } catch (e) { /* tenta de novo */ }
    }, 5000);
    return () => clearInterval(t);
  }, [processando, ataId, carregarAta, carregarSessoes]);

  const salvo = (det && det.ata && det.ata.textoAta) || '';
  const sujo = texto !== salvo;
  useEffect(() => { if (msgsRef.current) msgsRef.current.scrollTop = msgsRef.current.scrollHeight; }, [det, pensando, proposta]);

  const escolher = (id) => {
    if (sujo && !window.confirm('Há alterações na ata que ainda não foram salvas. Sair sem salvar?')) return;
    setSel(id); setAba('ata'); setArquivo(null);
    navigate(`/atas?sessao=${id}`, { replace: true });
  };

  const gravar = async (novo, origem, instrucao) => {
    setSalvando(true);
    try {
      await axios.put(`/api/ata-ia/${ataId}`, { textoAta: novo, origem, instrucao });
      setTexto(novo);
      await carregarAta(true);
      setDet(d => (d ? { ...d, ata: { ...d.ata, textoAta: novo } } : d));
      return true;
    } catch (e) { toast.error(erroDe(e, 'Erro ao salvar a ata.')); return false; }
    finally { setSalvando(false); }
  };
  const salvar = async () => { if (await gravar(texto, 'MANUAL')) toast.success('Ata salva. A versão anterior ficou guardada em "Versões".'); };

  const enviarAudio = async () => {
    if (!arquivo || !sessao) return;
    setEnviando(true);
    try {
      const fd = new FormData(); fd.append('audio', arquivo);
      const r = await axios.post(`/api/ata-ia/${sessao.id}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Arquivo enviado! A transcrição começou.');
      setArquivo(null); await carregarSessoes(); setAtaId(r.data.id);
    } catch (e) { toast.error(erroDe(e, 'Erro ao enviar o arquivo.')); }
    finally { setEnviando(false); }
  };
  const gerarDeNovo = async () => {
    if (!window.confirm('Gerar a ata de novo a partir da transcrição?\n\nO texto atual fica guardado em "Versões" e pode ser restaurado.')) return;
    try { await axios.post(`/api/ata-ia/${ataId}/montar`); toast.success('A IA está redigindo a ata de novo.'); setDet(d => ({ ...d, ata: { ...d.ata, status: 'MONTANDO' } })); }
    catch (e) { toast.error(erroDe(e, 'Não foi possível gerar a ata.')); }
  };

  const pedirIA = async () => {
    const instr = pedido.trim();
    if (!instr || pensando) return;
    setPensando(instr); setProposta(null);
    try {
      const r = await axios.post(`/api/ata-ia/${ataId}/ajustar`, { instrucao: instr, texto });
      setPedido('');
      const pid = r.data.pedido; const base = texto;
      for (let k = 0; k < 120; k++) {
        await new Promise(ok => setTimeout(ok, 3000));
        const s = await axios.get(`/api/ata-ia/ajuste/${pid}`);
        if (s.data.status === 'PRONTO') { setProposta({ base, texto: s.data.texto, resumo: s.data.resumo, instrucao: instr }); break; }
        if (s.data.status === 'ERRO') { toast.error(s.data.erro || 'A IA não conseguiu fazer o ajuste.'); break; }
      }
      carregarAta(true);
    } catch (e) { toast.error(erroDe(e, 'Não foi possível falar com a IA.')); }
    finally { setPensando(null); }
  };
  const aplicar = async () => { if (!proposta) return; if (await gravar(proposta.texto, 'IA', proposta.instrucao)) { toast.success('Alteração aplicada e salva.'); setProposta(null); } };

  const abrirVersao = async (v) => { try { const r = await axios.get(`/api/ata-ia/${ataId}/versoes/${v.id}`); setVerVersao(r.data); } catch (e) { toast.error(erroDe(e, 'Não foi possível abrir a versão.')); } };
  const restaurar = async () => {
    if (!verVersao || !window.confirm('Restaurar esta versão? O texto atual fica guardado em "Versões".')) return;
    if (await gravar(verVersao.texto, 'RESTAURADA', `Restaurada a versão de ${quando(verVersao.criadoEm)}`)) { toast.success('Versão restaurada.'); setVerVersao(null); setAba('ata'); }
  };

  const transcricao = (det && det.ata && det.ata.transcricao) || '';
  const baixarTranscricao = () => {
    const nome = `Transcrição - ${sessao ? sessao.titulo : 'sessão'}`.replace(/[\\/:*?"<>|]/g, ' ').trim();
    const cab = `TRANSCRIÇÃO DO ÁUDIO — ${sessao ? sessao.titulo : ''}\nCódigo de verificação (SHA-256): ${det && det.integridade ? det.integridade.codigo : ''}\n\n`;
    const u = URL.createObjectURL(new Blob([cab + transcricao], { type: 'text/plain;charset=utf-8' }));
    const a = document.createElement('a'); a.href = u; a.download = nome + '.txt'; document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(u), 3000);
  };
  const imprimirTranscricao = () => {
    const esc = (s) => String(s || '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    const w = window.open('', '_blank'); if (!w) { toast.error('Permita pop-ups para gerar o PDF.'); return; }
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Transcrição — ${esc(sessao && sessao.titulo)}</title><style>body{font-family:'Times New Roman',serif;padding:40px;max-width:820px;margin:auto;font-size:13px;line-height:1.7}h1{font-size:15px;text-align:center}p.c{font-size:10px;color:#555;text-align:center;word-break:break-all}div{white-space:pre-wrap;text-align:justify}</style></head><body><h1>TRANSCRIÇÃO DO ÁUDIO — ${esc((sessao && sessao.titulo) || '').toUpperCase()}</h1><p class="c">Transcrição automática na íntegra, sem edição. Código de verificação (SHA-256): ${esc(det && det.integridade ? det.integridade.codigo : '')}</p><div>${esc(transcricao)}</div></body></html>`);
    w.document.close(); setTimeout(() => w.print(), 500);
  };

  const lista = (sessoes || []).filter(s => !busca.trim() || semAcento(s.titulo + ' ' + dataBR(s.data)).includes(semAcento(busca.trim())));
  const stDe = (s) => { const a = (s.atas || [])[0]; if (!a) return ST.SEM; if (a.status === 'CONCLUIDA' && !a.temAta) return ['Só transcrição', '#0369a1', '#e0f2fe']; return ST[a.status] || ST.SEM; };

  return (
    <div>
      <style>{CSS}</style>
      <div className="page-header">
        <div>
          <h1 className="page-title">Central de Atas</h1>
          <p className="page-subtitle">Ata de cada sessão: gerar, editar, ajustar com a IA, transcrição na íntegra, PDF e Word</p>
        </div>
      </div>
      <div className="pca">
        <div className="pca-lista">
          <div className="pca-busca"><Search size={14} /><input value={busca} onChange={e => setBusca(e.target.value)} placeholder="Buscar sessão" /></div>
          {!sessoes ? <div className="pca-vazio">Carregando…</div> : !lista.length ? <div className="pca-vazio">Nenhuma sessão.</div> : lista.map(s => {
            const st = stDe(s);
            return (
              <button key={s.id} type="button" className={`pca-s ${s.id === sel ? 'on' : ''}`} onClick={() => escolher(s.id)}>
                <b>{s.titulo}</b>
                <small>{dataBR(s.data)}</small><span className="pca-chip" style={{ color: st[1], background: st[2] }}>{st[0]}</span>
              </button>
            );
          })}
        </div>

        <div>
          {!sessao ? (
            <div className="pca-card pca-vazio"><BookOpen size={36} style={{ opacity: .4 }} /><p>Escolha uma sessão na lista.</p></div>
          ) : (
            <div className="pca-card">
              <div className="pca-topo">
                <div>
                  <h2>{sessao.titulo}</h2>
                  <small>{dataBR(sessao.data)}{atasDaSessao.length > 1 ? ` · ${atasDaSessao.length} gravações enviadas` : ''}</small>
                  {atasDaSessao.length > 1 && (
                    <div style={{ marginTop: 6 }}>
                      <select className="form-input" style={{ maxWidth: 360, padding: '6px 8px' }} value={ataId || ''} onChange={e => { if (sujo && !window.confirm('Sair sem salvar?')) return; setAtaId(e.target.value); }}>
                        {atasDaSessao.map(a => <option key={a.id} value={a.id}>{quando(a.criadoEm)} — {a.fonteRef || 'gravação'} ({(ST[a.status] || ST.SEM)[0]})</option>)}
                      </select>
                    </div>
                  )}
                </div>
                {det && det.ata && det.ata.textoAta && (
                  <div className="pca-bts">
                    <button className="btn btn-secondary btn-sm" onClick={() => gerarPDFAtaIA(sessao, texto)}><Download size={14} /> PDF</button>
                    <button className="btn btn-secondary btn-sm" onClick={() => gerarWordAtaIA(sessao, texto)}><FileText size={14} /> Word</button>
                    <button className="btn btn-primary btn-sm" onClick={salvar} disabled={salvando || !sujo}><Save size={14} /> {salvando ? 'Salvando…' : sujo ? 'Salvar' : 'Salvo'}</button>
                  </div>
                )}
              </div>

              {/* enviar gravacao */}
              {(!ataId || status === 'ERRO') && (
                <div className="pca-card" style={{ background: '#f8fafc', marginBottom: 12 }}>
                  <p style={{ fontSize: '.85rem', margin: '0 0 8px' }}>{ataId ? 'Houve um erro nesta gravação. Envie de novo, ou gere a ata outra vez se a transcrição já estiver pronta.' : 'Envie o áudio ou vídeo da sessão. O sistema transcreve na íntegra e a IA redige a ata com os dados da sessão (pautas, votos, presenças).'}</p>
                  {det && det.ata && det.ata.erro && <p style={{ color: '#b91c1c', fontSize: '.8rem' }}>{det.ata.erro}</p>}
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                    <input type="file" accept="audio/*,video/*,.mp3,.m4a,.wav,.ogg,.mp4,.mkv,.mov,.webm" className="form-input" style={{ maxWidth: 380 }} onChange={e => setArquivo(e.target.files[0] || null)} />
                    <button className="btn btn-primary btn-sm" disabled={!arquivo || enviando} onClick={enviarAudio}><Upload size={14} /> {enviando ? 'Enviando…' : 'Enviar e gerar'}</button>
                    {transcricao && <button className="btn btn-secondary btn-sm" onClick={gerarDeNovo}><Sparkles size={14} /> Gerar a ata de novo</button>}
                  </div>
                </div>
              )}
              {processando && (
                <div className="pca-vazio"><div className="spinner" style={{ margin: '0 auto 12px' }} /><b>{status === 'MONTANDO' ? 'A IA está redigindo a ata…' : 'Transcrevendo o áudio…'}</b><p style={{ fontSize: '.82rem' }}>Pode levar alguns minutos. Esta tela atualiza sozinha.</p></div>
              )}

              {det && det.ata && !processando && (det.ata.textoAta || transcricao) && (
                <>
                  <div className="pca-abas">
                    {[['ata', 'Ata'], ['transcricao', 'Transcrição (íntegra)'], ['versoes', `Versões (${(det.versoes || []).length})`]].map(([k, t]) => (
                      <button key={k} type="button" className={`pca-aba ${aba === k ? 'on' : ''}`} onClick={() => setAba(k)}>{t}</button>
                    ))}
                    <span style={{ flex: 1 }} />
                    {transcricao && status !== 'ERRO' && <button type="button" className="pca-aba" onClick={gerarDeNovo} title="Redigir a ata de novo a partir da transcrição"><RotateCcw size={13} /> Gerar de novo</button>}
                  </div>

                  {aba === 'ata' && (
                    <div className="pca-edit">
                      <div>
                        {proposta && (
                          <div className="pca-prop">
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
                              <b style={{ color: '#5b21b6' }}><Sparkles size={14} /> Proposta da IA: {proposta.resumo}</b>
                              <div className="pca-bts">
                                <button className="btn btn-secondary btn-sm" onClick={() => setProposta(null)}><X size={14} /> Descartar</button>
                                <button className="btn btn-primary btn-sm" onClick={aplicar} disabled={salvando}><Check size={14} /> Aplicar</button>
                              </div>
                            </div>
                            <MostrarDiff antes={proposta.base} depois={proposta.texto} />
                          </div>
                        )}
                        <textarea className="pca-texto" value={texto} onChange={e => setTexto(e.target.value)} placeholder="Texto da ata" />
                        {sujo && <p style={{ fontSize: '.78rem', color: '#b45309', marginTop: 4 }}>Alterações ainda não salvas.</p>}
                      </div>
                      <div className="pca-chat">
                        <div className="pca-chat-cab"><Sparkles size={16} /> Conversar com a IA</div>
                        <div className="pca-msgs" ref={msgsRef}>
                          {!(det.conversa || []).length && !pensando && <p style={{ color: '#64748b', fontSize: '.8rem' }}>Diga o que precisa mudar. Exemplos:<br />• "Corrija o nome do vereador Fulano para Fulano de Tal"<br />• "Inclua que a sessão foi suspensa por 10 minutos às 20h"<br />• "Apague o parágrafo sobre a iluminação pública"<br /><br />A IA mostra o que muda e você aplica ou descarta.</p>}
                          {(det.conversa || []).map(m => <div key={m.id} className={`pca-msg ${m.papel === 'IA' ? 'ia' : 'u'}`}>{m.texto}<small>{m.papel === 'IA' ? 'IA' : (m.por || 'Você')} · {quando(m.criadoEm)}</small></div>)}
                          {pensando && <div className="pca-msg ia"><i>Ajustando a ata… isso leva até 1 minuto.</i></div>}
                        </div>
                        <div className="pca-enviar">
                          <textarea value={pedido} onChange={e => setPedido(e.target.value)} placeholder="O que precisa mudar na ata?" disabled={!!pensando || !texto.trim()}
                            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); pedirIA(); } }} />
                          <button className="btn btn-primary btn-sm" onClick={pedirIA} disabled={!!pensando || !pedido.trim() || !texto.trim()} title="Enviar"><Send size={15} /></button>
                        </div>
                      </div>
                    </div>
                  )}

                  {aba === 'transcricao' && (
                    <div>
                      {transcricao ? (
                        <>
                          <div className="pca-selo" style={det.integridade && det.integridade.confere ? { background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#14532d' } : { background: '#fef2f2', border: '1px solid #fecaca', color: '#7f1d1d' }}>
                            <Lock size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                            <div>
                              <b>{det.integridade && det.integridade.confere ? 'Transcrição original do áudio — não pode ser editada.' : 'Atenção: o código de verificação não confere com o registrado.'}</b><br />
                              Código de verificação (SHA-256): <code style={{ wordBreak: 'break-all' }}>{det.integridade ? det.integridade.codigo : '—'}</code>
                              {det.integridade && det.integridade.registradoEm && <> · registrado em {quando(det.integridade.registradoEm)}</>}
                            </div>
                          </div>
                          <div className="pca-bts" style={{ marginBottom: 8 }}>
                            <button className="btn btn-secondary btn-sm" onClick={() => { navigator.clipboard.writeText(transcricao); toast.success('Transcrição copiada.'); }}><Copy size={14} /> Copiar</button>
                            <button className="btn btn-secondary btn-sm" onClick={baixarTranscricao}><Download size={14} /> Baixar (.txt)</button>
                            <button className="btn btn-secondary btn-sm" onClick={imprimirTranscricao}><FileText size={14} /> PDF</button>
                          </div>
                          <div className="pca-trans">{transcricao}</div>
                        </>
                      ) : <div className="pca-vazio">Sem transcrição nesta gravação.</div>}
                    </div>
                  )}

                  {aba === 'versoes' && (
                    <div>
                      {verVersao ? (
                        <div>
                          <div className="pca-topo">
                            <div><b>Versão de {quando(verVersao.criadoEm)}</b><br /><small>Texto como estava antes de: {ORIGEM[verVersao.origem] || verVersao.origem}{verVersao.por ? ` (${verVersao.por})` : ''}{verVersao.instrucao ? ` — “${verVersao.instrucao}”` : ''}</small></div>
                            <div className="pca-bts">
                              <button className="btn btn-secondary btn-sm" onClick={() => setVerVersao(null)}><X size={14} /> Voltar</button>
                              <button className="btn btn-primary btn-sm" onClick={restaurar} disabled={salvando}><RotateCcw size={14} /> Restaurar esta versão</button>
                            </div>
                          </div>
                          <p style={{ fontSize: '.8rem', color: '#64748b' }}>Diferença para o texto atual da ata:</p>
                          <MostrarDiff antes={verVersao.texto} depois={salvo} />
                        </div>
                      ) : !(det.versoes || []).length ? <div className="pca-vazio">Nenhuma versão anterior ainda. Cada vez que a ata for salva, ajustada pela IA ou gerada de novo, o texto anterior fica guardado aqui.</div>
                        : (det.versoes || []).map(v => (
                          <div key={v.id} className="pca-ver">
                            <div><b style={{ fontSize: '.88rem' }}>{quando(v.criadoEm)}</b> <small style={{ color: '#64748b' }}>· antes de: {ORIGEM[v.origem] || v.origem}{v.por ? ` (${v.por})` : ''}</small>{v.instrucao && <div style={{ fontSize: '.78rem', color: '#475569' }}>“{v.instrucao}”</div>}</div>
                            <button className="btn btn-secondary btn-sm" onClick={() => abrirVersao(v)}>Ver</button>
                          </div>
                        ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
